"use client"

import { PieChart, Pie, Cell, ResponsiveContainer, Label } from "recharts"
import type { CSSProperties } from "react"
import type { ChartDimension } from "./types"

/** A half-circle gauge built from a Pie. Useful for KPIs like utilisation,
 *  score-to-target, or any 0..max scalar. */
export interface GaugeChartProps {
  value: number
  max?: number
  /** Color the filled arc — default emerald. */
  color?: string
  /** Color the unfilled track — default a translucent gray. */
  trackColor?: string
  /** Centre text. When omitted, displays `value`. */
  label?: string
  /** Optional sub-label below the main label. */
  sublabel?: string
  className?: string
  style?: CSSProperties
  height?: ChartDimension
  width?: ChartDimension
}

export function GaugeChart({
  value, max = 100,
  color = "#10b981", trackColor = "rgba(125,125,125,0.15)",
  label, sublabel,
  className, style, height = "100%", width = "100%",
}: GaugeChartProps) {
  const clamped = Math.max(0, Math.min(value, max))
  const data = [
    { name: "value", v: clamped, fill: color },
    { name: "rest",  v: max - clamped, fill: trackColor },
  ]
  return (
    <ResponsiveContainer width={width} height={height} className={className} style={style}>
      <PieChart>
        <Pie
          data={data}
          dataKey="v"
          startAngle={180}
          endAngle={0}
          cx="50%" cy="75%"
          outerRadius="100%"
          innerRadius="60%"
          stroke="none"
          isAnimationActive={false}
        >
          {data.map((d, i) => <Cell key={i} fill={d.fill} />)}
          <Label
            value={label ?? String(clamped)}
            position="center"
            style={{ fontSize: 18, fontWeight: 700, fill: "currentColor", transform: "translateY(-12px)" }}
          />
          {sublabel && (
            <Label
              value={sublabel}
              position="center"
              style={{ fontSize: 10, fill: "currentColor", opacity: 0.6, transform: "translateY(8px)" }}
            />
          )}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  )
}
